import dayjs from "dayjs";

export const MAX_PENDING_PER_TYPE = 5;
export const MAX_SERIES_ENTRIES = 20;
export const AVAILABLE_TABS = [
  "dashboard",
  "footprint",
  "products",
  "customers",
  "suppliers"
];

export const SECTION_STATUS_LABELS = {
  complete: "Done",
  skipped: "Skipped",
  unknown: "Marked unknown",
  pending: "Not started yet"
};

export const buildPendingSurveys = (customers) =>
  (customers || [])
    .filter(({ surveyData }) =>
      (surveyData || []).some(({ status }) => status === "requested")
    )
    .map((customer) => {
      const requestedEntry = (customer.surveyData || []).find(
        ({ status }) => status === "requested"
      );
      return {
        id: customer.id,
        buyerName: customer.buyerName,
        year: requestedEntry?.year
      };
    });

export const buildPendingProducts = (productRequests) =>
  (productRequests || [])
    .filter(({ bomStatus }) => bomStatus === "requested")
    .map(({ id, productName, ownerName }) => ({
      id,
      productName: productName || "Unnamed Product",
      ownerName
    }));

export const findSurveyEntry = (surveys, year) =>
  (surveys || []).find(({ year: entryYear }) => Number(entryYear) === year);

export const deriveSurveyStatus = (
  surveys,
  year,
  totalSurveyTons,
  completenessSummary
) => {
  const entry = findSurveyEntry(surveys, year);
  if (!entry) {
    return "none";
  }
  const hasEstimate =
    Number(entry.tonsCo2ePerDollar) > 0 || totalSurveyTons > 0;
  const totalCount = Number(completenessSummary?.totalCount) || 0;
  const completedCount = Number(completenessSummary?.completedCount) || 0;
  const skippedCount = Number(completenessSummary?.skippedCount) || 0;
  const unknownCount = Number(completenessSummary?.unknownCount) || 0;
  const resolvedCount =
    Number(completenessSummary?.resolvedCount) ||
    completedCount + skippedCount + unknownCount;
  const allSectionsResolved = totalCount > 0 && resolvedCount >= totalCount;
  if (hasEstimate && allSectionsResolved) {
    return "complete";
  }
  if (hasEstimate) {
    return "initial-estimate-ready";
  }
  return "partial";
};

export const toMillis = (value) => {
  if (!value) {
    return 0;
  }
  if (typeof value === "number") {
    return value;
  }
  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }
  if (value.seconds) {
    return value.seconds * 1000;
  }
  return new Date(value).getTime() || 0;
};

const findRequestedRelationship = (customers) =>
  (customers || [])
    .filter((rel) =>
      (rel.surveyData || []).some(({ status }) => status === "requested")
    )
    .map((rel) => {
      const requestedEntry = (rel.surveyData || []).find(
        ({ status }) => status === "requested"
      );
      return { rel, requestedAt: toMillis(requestedEntry?.dateRequested) };
    })
    .sort((a, b) => b.requestedAt - a.requestedAt)[0]?.rel;

export const resolveBuyerInvite = (urlBuyerId, customers) => {
  if (urlBuyerId) {
    const matching = (customers || []).find(
      ({ buyerId }) => buyerId === urlBuyerId
    );
    return {
      buyerInviteId: urlBuyerId,
      buyerName: matching?.buyerName || null
    };
  }
  const fallback = findRequestedRelationship(customers);
  if (!fallback) {
    return { buyerInviteId: null, buyerName: null };
  }
  return {
    buyerInviteId: fallback.buyerId || null,
    buyerName: fallback.buyerName || null
  };
};

export const parseViewParam = (search) => {
  const params = new URLSearchParams(search || "");
  return params.get("view");
};

export const isValidTab = (tab) => AVAILABLE_TABS.includes(tab);

export const filterEmissionsForYear = (allEmissions, year) => {
  if (!year) {
    return [];
  }
  return (allEmissions || []).filter(({ date }) => {
    const dateObj = date?.toDate ? date.toDate() : date;
    return dayjs(dateObj).year() === year;
  });
};

export const sumTons = (entries) =>
  (entries || []).reduce(
    (sum, { tonsCo2e = 0 }) => sum + (Number(tonsCo2e) || 0),
    0
  );

const TOP_SUPPLIERS_LIMIT = 5;

export const buildTopSupplierEmissions = (surveyEmissions) => {
  const supplierTons = (surveyEmissions || [])
    .filter(({ source }) => source === "vendor-survey")
    .reduce((acc, { description, tonsCo2e = 0 }) => {
      const name = (description || "").trim();
      if (!name) {
        return acc;
      }
      const tons = Number(tonsCo2e) || 0;
      return { ...acc, [name]: (acc[name] || 0) + tons };
    }, {});

  return Object.entries(supplierTons)
    .map(([name, tons]) => ({ name, tons }))
    .sort((a, b) => b.tons - a.tons)
    .slice(0, TOP_SUPPLIERS_LIMIT);
};

export const countSurveySuppliers = (surveyEmissions) => {
  const names = (surveyEmissions || [])
    .filter(({ source }) => source === "vendor-survey")
    .map(({ description }) => (description || "").trim())
    .filter(Boolean);
  return new Set(names).size;
};

export const computeTotalTons = ({
  yearSurveyEntry,
  revenueYears,
  activeYear,
  surveyEmissions
}) => {
  const perDollar = Number(yearSurveyEntry?.tonsCo2ePerDollar) || 0;
  const yearRevenue =
    Number(
      (revenueYears || []).find(({ year }) => Number(year) === activeYear)
        ?.revenue
    ) || 0;
  const headline = perDollar * yearRevenue;
  if (headline > 0) {
    return headline;
  }
  return sumTons(surveyEmissions);
};

export const humanizeSubcategory = (sub) =>
  (sub || "Other")
    .toString()
    .split(/[-_\s]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const buildScopeSeries = (surveyEmissions) =>
  Object.values(
    (surveyEmissions || []).reduce((acc, { scope, tonsCo2e }) => {
      const key = scope ? `Scope ${scope}` : "Unscoped";
      const priorValue = acc[key]?.value || 0;
      return {
        ...acc,
        [key]: {
          label: key,
          value: priorValue + (Number(tonsCo2e) || 0)
        }
      };
    }, {})
  )
    .filter(({ value }) => value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, MAX_SERIES_ENTRIES);

const SCOPE_KEYS = new Set([1, 2, 3]);

export const resolveDisplayTotalTons = ({ totalTons, surveyEmissions }) => {
  const scopesSum = (surveyEmissions || []).reduce(
    (sum, { scope, tonsCo2e }) =>
      SCOPE_KEYS.has(Number(scope)) ? sum + (Number(tonsCo2e) || 0) : sum,
    0
  );
  return scopesSum > 0 ? scopesSum : Number(totalTons) || 0;
};

export const buildSourceSeries = (surveyEmissions) =>
  Object.values(
    (surveyEmissions || []).reduce((acc, { subcategory, tonsCo2e }) => {
      const key = subcategory || "other";
      const priorValue = acc[key]?.value || 0;
      return {
        ...acc,
        [key]: {
          label: humanizeSubcategory(subcategory),
          value: priorValue + (Number(tonsCo2e) || 0),
          categoryKey: key
        }
      };
    }, {})
  )
    .filter(({ value }) => value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, MAX_SERIES_ENTRIES);

export const buildHotspots = (sourceSeries, totalTons) =>
  (sourceSeries || []).slice(0, 3).map(({ label, value, categoryKey }) => ({
    label,
    tons: value,
    share: totalTons > 0 ? (value / totalTons) * 100 : 0,
    categoryKey
  }));

export const buildCompletenessSummary = (surveyProgress) => {
  if (!surveyProgress) {
    return {
      percent: 0,
      completedCount: 0,
      totalCount: 0,
      resolvedCount: 0,
      pendingCount: 0,
      skippedCount: 0,
      unknownCount: 0,
      remainingSectionNames: [],
      items: []
    };
  }
  const {
    sections = [],
    completedSections = [],
    skippedSections = [],
    unknownSections = [],
    pendingSections = []
  } = surveyProgress;
  const total = sections.length || 1;
  const resolvedCount =
    completedSections.length + skippedSections.length + unknownSections.length;
  return {
    percent: Math.round((resolvedCount / total) * 100),
    completedCount: completedSections.length,
    totalCount: sections.length,
    resolvedCount,
    pendingCount: pendingSections.length,
    skippedCount: skippedSections.length,
    unknownCount: unknownSections.length,
    remainingSectionNames: pendingSections,
    items: sections.map(({ name, status }) => ({
      label: name,
      value: SECTION_STATUS_LABELS[status] || status
    }))
  };
};

export const buildNextBestAction = (surfaceContext) => {
  const { surveyStatus, year, pendingRequestsSummary } = surfaceContext || {};
  const surveyHref = year
    ? `/explorer/emissions-survey?year=${year}`
    : "/explorer/emissions-survey";

  const incompleteLabels = {
    partial: "Continue your estimate",
    "initial-estimate-ready": "Complete your estimate",
    none: "Start your estimate"
  };
  if (surveyStatus !== "complete") {
    return {
      label: incompleteLabels[surveyStatus] || incompleteLabels.none,
      description: "Improve the accuracy and confidence of your footprint.",
      href: surveyHref
    };
  }
  if ((pendingRequestsSummary?.surveyCount || 0) > 0) {
    return {
      label: "Respond to your customers",
      description: "Customers are waiting on your emissions data.",
      href: "/explorer/dashboard?view=customers"
    };
  }
  if ((pendingRequestsSummary?.productCount || 0) > 0) {
    return {
      label: "Confirm product data",
      description: "Share the footprint of products customers asked about.",
      href: "/explorer/dashboard?view=products"
    };
  }
  return {
    label: "Explore carbon offsets",
    description: "Offset your footprint and take the next step.",
    href: "/explorer/dashboard/offsets"
  };
};
